import React, { useState, useEffect, useCallback } from 'react'
import Footer from '../components/footer'
import Navbar from '../components/navbar'
import Button from '../components/input-components/button'
import TextField from '../components/input-components/textfield'
import TextArea from '../components/input-components/textarea'
import {
  PAYMENT_PLATFORMS,
  PaymentPlatform,
  DeviceType,
  getDisplayPlatformName,
} from '../types/donation'
import { transactionsApi, Transaction, TransactionFilter, BillingDetails, GiftAidDetails, TransactionUpdate } from '../services/transactions-api'
import { donationsApi, DonationType } from '../services/donations-api'
import { campaignsApi, Campaign } from '../services/campaigns-api'

// Platforms that can be manually recorded
const MANUAL_PLATFORMS: { id: PaymentPlatform; name: string }[] = [
  { id: 'bank_transfer', name: 'Bank Transfer' },
  { id: 'stripe', name: 'Stripe' },
]

// Helper to format currency (amount is in pence)
const formatCurrency = (amountInPence: number): string => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amountInPence / 100)
}

// Helper to format date
const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

// Helper to format time
const formatTime = (dateStr: string): string => {
  const date = new Date(dateStr)
  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Platform colors and icons
const platformStyles: Record<PaymentPlatform, { bg: string; text: string; border: string }> = {
  apple_pay: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300' },
  google_pay: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  paypal: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
  bank_transfer: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  stripe: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
}

// Get style for platform+device combination (for display differentiation)
const getPlatformStyle = (platform: PaymentPlatform, device?: DeviceType) => {
  // For Stripe with device, use device-specific colors
  if (platform === 'stripe' && device) {
    if (device === 'ios') {
      return { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300' } // Apple Pay style
    }
    if (device === 'android') {
      return { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' } // Google Pay style
    }
  }
  return platformStyles[platform] || { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' }
}

// Record Transaction Form Component
interface RecordTransactionFormProps {
  donationTypes: DonationType[]
  onSave: (data: { amount: number; platform: PaymentPlatform; donationTypeId: string; date: string; reference?: string; billing?: BillingDetails; giftAid?: GiftAidDetails }) => void
  onCancel: () => void
  isSaving: boolean
}

const RecordTransactionForm: React.FC<RecordTransactionFormProps> = ({
  donationTypes,
  onSave,
  onCancel,
  isSaving,
}) => {
  const [platform, setPlatform] = useState<PaymentPlatform>('bank_transfer')
  const [amount, setAmount] = useState('')
  const [donationTypeId, setDonationTypeId] = useState('')
  const [date, setDate] = useState(new Date().toISOString().split('T')[0])
  const [reference, setReference] = useState('')

  // Donor details (optional, but required for Gift Aid)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [addressLine1, setAddressLine1] = useState('')
  const [addressLine2, setAddressLine2] = useState('')
  const [city, setCity] = useState('')
  const [postcode, setPostcode] = useState('')

  // Gift Aid
  const [giftAidOptIn, setGiftAidOptIn] = useState(false)

  const handleSubmit = () => {
    const amountInPence = Math.round((parseFloat(amount) || 0) * 100)

    const billing: BillingDetails | undefined = (firstName || lastName || addressLine1 || city || postcode)
      ? {
          firstName: firstName || undefined,
          lastName: lastName || undefined,
          addressLine1: addressLine1 || undefined,
          addressLine2: addressLine2 || undefined,
          city: city || undefined,
          postcode: postcode || undefined,
        }
      : undefined

    const giftAid: GiftAidDetails | undefined = giftAidOptIn
      ? {
          optedIn: true,
          firstName,
          lastName,
          addressLine1,
          addressLine2: addressLine2 || undefined,
          city,
          postcode,
        }
      : undefined

    onSave({
      amount: amountInPence,
      platform,
      donationTypeId,
      date,
      reference: reference || undefined,
      billing,
      giftAid,
    })
  }

  const giftAidFieldsValid = !giftAidOptIn || (firstName && lastName && addressLine1 && city && postcode)
  const isValid = amount && parseFloat(amount) > 0 && donationTypeId && date && giftAidFieldsValid

  return (
    <div className="bg-white rounded-lg border border-teal-200 p-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Record Transaction</h3>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Platform *</label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value as PaymentPlatform)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              {MANUAL_PLATFORMS.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <TextField
            id="amount"
            type="number"
            label="Amount (£)"
            placeholder="e.g. 100"
            value={amount}
            setValue={setAmount}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Donation Type *</label>
            <select
              value={donationTypeId}
              onChange={(e) => setDonationTypeId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="">Select donation type...</option>
              {donationTypes.filter(dt => dt.isActive).map((dt) => (
                <option key={dt.id} value={dt.id}>{dt.name}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              If linked to a campaign, the donation will be automatically tracked.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
        </div>

        <TextArea
          id="reference"
          type="text"
          label="Reference / Notes"
          placeholder="e.g. Bank reference number, notes, etc."
          value={reference}
          setValue={setReference}
        />

        {/* Gift Aid Section */}
        <div className="border-t border-gray-200 pt-4 mt-4">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="giftAid"
              checked={giftAidOptIn}
              onChange={(e) => setGiftAidOptIn(e.target.checked)}
              className="mt-1 h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
            />
            <div>
              <label htmlFor="giftAid" className="text-sm font-medium text-gray-700 cursor-pointer">
                Claim Gift Aid on this donation
              </label>
              <p className="text-xs text-gray-500 mt-1">
                If the donor is a UK taxpayer, Gift Aid increases the donation value by 25% at no extra cost to them.
              </p>
            </div>
          </div>
        </div>

        {/* Donor Details Section */}
        <div className={`border-t border-gray-200 pt-4 mt-4 ${giftAidOptIn ? 'bg-amber-50 -mx-6 px-6 py-4 border-amber-200' : ''}`}>
          <h4 className="text-sm font-medium text-gray-700 mb-1">
            Donor Details {giftAidOptIn ? <span className="text-red-500">*</span> : '(Optional)'}
          </h4>
          <p className="text-xs text-gray-500 mb-3">
            {giftAidOptIn
              ? 'Required for Gift Aid. Please enter the donor\'s full name and UK address.'
              : 'Add donor information if known. Required if claiming Gift Aid.'}
          </p>

          <div className="grid grid-cols-2 gap-3">
            <TextField
              id="firstName"
              type="text"
              label="First Name"
              placeholder="John"
              value={firstName}
              setValue={setFirstName}
            />
            <TextField
              id="lastName"
              type="text"
              label="Last Name"
              placeholder="Smith"
              value={lastName}
              setValue={setLastName}
            />
          </div>

          <div className="mt-3">
            <TextField
              id="addressLine1"
              type="text"
              label="Address Line 1"
              placeholder="123 Main Street"
              value={addressLine1}
              setValue={setAddressLine1}
            />
          </div>

          <div className="mt-3">
            <TextField
              id="addressLine2"
              type="text"
              label="Address Line 2"
              placeholder="Apartment, suite, etc. (optional)"
              value={addressLine2}
              setValue={setAddressLine2}
            />
          </div>

          <div className="grid grid-cols-2 gap-3 mt-3">
            <TextField
              id="city"
              type="text"
              label="City"
              placeholder="London"
              value={city}
              setValue={setCity}
            />
            <TextField
              id="postcode"
              type="text"
              label="Postcode"
              placeholder="SW1A 1AA"
              value={postcode}
              setValue={setPostcode}
            />
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            id="saveTransaction"
            label="Record Donation"
            isLoading={isSaving}
            onClick={handleSubmit}
            disabled={!isValid || isSaving}
          />
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
            disabled={isSaving}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

interface TransactionCardProps {
  transaction: Transaction
  donationTypes: DonationType[]
  campaigns: Campaign[]
  onDelete?: () => void
  onUpdate?: (id: string, update: TransactionUpdate) => Promise<void>
  onGenerateReceipt?: (transactionId: string) => Promise<void>
  onCreateGiftAid?: (transactionId: string, giftAid: GiftAidDetails) => Promise<void>
  onViewGiftAid?: (declarationId: string) => void
  onViewReceipt?: (transactionId: string) => void
}

const TransactionCard: React.FC<TransactionCardProps> = ({
  transaction,
  donationTypes,
  campaigns,
  onDelete,
  onUpdate,
  onGenerateReceipt,
  onCreateGiftAid,
  onViewGiftAid,
  onViewReceipt,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [showGiftAidForm, setShowGiftAidForm] = useState(false)
  const [isGeneratingReceipt, setIsGeneratingReceipt] = useState(false)
  const [isCreatingGiftAid, setIsCreatingGiftAid] = useState(false)

  // Edit form state
  const [editFirstName, setEditFirstName] = useState(transaction.billing?.firstName || '')
  const [editLastName, setEditLastName] = useState(transaction.billing?.lastName || '')
  const [editEmail, setEditEmail] = useState(transaction.billing?.email || '')
  const [editPhone, setEditPhone] = useState(transaction.billing?.phone || '')
  const [editAddressLine1, setEditAddressLine1] = useState(transaction.billing?.addressLine1 || '')
  const [editAddressLine2, setEditAddressLine2] = useState(transaction.billing?.addressLine2 || '')
  const [editCity, setEditCity] = useState(transaction.billing?.city || '')
  const [editPostcode, setEditPostcode] = useState(transaction.billing?.postcode || '')

  // Gift Aid form state
  const [giftAidTitle, setGiftAidTitle] = useState('')
  const [giftAidFirstName, setGiftAidFirstName] = useState(transaction.billing?.firstName || '')
  const [giftAidLastName, setGiftAidLastName] = useState(transaction.billing?.lastName || '')
  const [giftAidAddressLine1, setGiftAidAddressLine1] = useState(transaction.billing?.addressLine1 || '')
  const [giftAidAddressLine2, setGiftAidAddressLine2] = useState(transaction.billing?.addressLine2 || '')
  const [giftAidCity, setGiftAidCity] = useState(transaction.billing?.city || '')
  const [giftAidPostcode, setGiftAidPostcode] = useState(transaction.billing?.postcode || '')
  const [giftAidEmail, setGiftAidEmail] = useState(transaction.billing?.email || '')

  const donationType = donationTypes.find((dt) => dt.id === transaction.donationTypeId)
  const campaign = campaigns.find((c) => c.id === transaction.campaignId)
  const displayPlatformName = getDisplayPlatformName(transaction.platform, transaction.device)
  const styles = getPlatformStyle(transaction.platform, transaction.device)
  const isManualPlatform = MANUAL_PLATFORMS.some(p => p.id === transaction.platform)

  const hasAddress = transaction.billing?.addressLine1 || transaction.billing?.city || transaction.billing?.postcode
  const fullName = [transaction.billing?.firstName, transaction.billing?.lastName].filter(Boolean).join(' ')
  const addressParts = [
    transaction.billing?.addressLine1,
    transaction.billing?.addressLine2,
    transaction.billing?.city,
    transaction.billing?.postcode,
  ].filter(Boolean)

  const isRecurring = transaction.frequency && transaction.frequency !== 'one_time'
  const hasExpandableContent = hasAddress || transaction.reference || transaction.externalId || transaction.transactionCode || isRecurring

  const handleSaveEdit = async () => {
    if (!onUpdate) return
    setIsUpdating(true)
    try {
      await onUpdate(transaction.id, {
        billing: {
          firstName: editFirstName || undefined,
          lastName: editLastName || undefined,
          email: editEmail || undefined,
          phone: editPhone || undefined,
          addressLine1: editAddressLine1 || undefined,
          addressLine2: editAddressLine2 || undefined,
          city: editCity || undefined,
          postcode: editPostcode || undefined,
        },
      })

      // If Gift Aid was previously claimed and we have complete address info, regenerate it
      if (transaction.giftAidDeclarationId && onCreateGiftAid && editFirstName && editLastName && editAddressLine1 && editCity && editPostcode) {
        try {
          await onCreateGiftAid(transaction.id, {
            optedIn: true,
            firstName: editFirstName,
            lastName: editLastName,
            addressLine1: editAddressLine1,
            addressLine2: editAddressLine2 || undefined,
            city: editCity,
            postcode: editPostcode,
            email: editEmail || undefined,
          })
        } catch (giftAidErr) {
          console.error('Failed to regenerate Gift Aid:', giftAidErr)
          // Don't fail the whole operation if Gift Aid regeneration fails
        }
      }

      setIsEditing(false)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleGenerateReceipt = async () => {
    if (!onGenerateReceipt) return
    setIsGeneratingReceipt(true)
    try {
      await onGenerateReceipt(transaction.id)
    } finally {
      setIsGeneratingReceipt(false)
    }
  }

  const handleCreateGiftAid = async () => {
    if (!onCreateGiftAid) return
    setIsCreatingGiftAid(true)
    try {
      await onCreateGiftAid(transaction.id, {
        optedIn: true,
        title: giftAidTitle || undefined,
        firstName: giftAidFirstName,
        lastName: giftAidLastName,
        addressLine1: giftAidAddressLine1,
        addressLine2: giftAidAddressLine2 || undefined,
        city: giftAidCity,
        postcode: giftAidPostcode,
        email: giftAidEmail || undefined,
      })
      setShowGiftAidForm(false)
    } finally {
      setIsCreatingGiftAid(false)
    }
  }

  const giftAidFormValid = giftAidFirstName && giftAidLastName && giftAidAddressLine1 && giftAidCity && giftAidPostcode

  return (
    <div className={`bg-white rounded-lg border ${styles.border} overflow-hidden`}>
      {/* Main Row */}
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <span className="text-xl font-bold text-gray-900">
                {formatCurrency(transaction.amount)}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded ${styles.bg} ${styles.text}`}>
                {displayPlatformName}
              </span>
              {transaction.giftAid && (
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Gift Aid
                </span>
              )}
              {transaction.frequency && transaction.frequency !== 'one_time' && (
                <span className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${
                  transaction.subscriptionStatus === 'active' ? 'bg-teal-100 text-teal-700' :
                  transaction.subscriptionStatus === 'cancelled' ? 'bg-gray-100 text-gray-500' :
                  transaction.subscriptionStatus === 'payment_failed' ? 'bg-red-100 text-red-700' :
                  'bg-teal-100 text-teal-700'
                }`}>
                  {transaction.frequency.charAt(0).toUpperCase() + transaction.frequency.slice(1)}
                  {transaction.subscriptionStatus === 'cancelled' && ' (Cancelled)'}
                  {transaction.subscriptionStatus === 'payment_failed' && ' (Failed)'}
                </span>
              )}
              {transaction.receiptS3Key && (
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Receipt
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
              {/* Donation Type */}
              <div className="flex items-center gap-1">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                <span>{donationType?.name || transaction.donationTypeId}</span>
              </div>

              {/* Date & Time */}
              <div className="flex items-center gap-1">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>{formatDate(transaction.date)}</span>
                <span className="text-gray-400">at</span>
                <span>{formatTime(transaction.createdAt)}</span>
              </div>

              {/* Campaign */}
              {campaign && (
                <div className="flex items-center gap-1">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                  <span>{campaign.title}</span>
                </div>
              )}

              {/* Donor Name */}
              {fullName && (
                <div className="flex items-center gap-1">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>{fullName}</span>
                </div>
              )}

              {/* Subscription Reference */}
              {transaction.subscriptionReference && (
                <div className="flex items-center gap-1">
                  <span className="text-teal-600 font-medium">{transaction.subscriptionReference}</span>
                </div>
              )}

              {/* Next Charge Date */}
              {transaction.nextChargeDate && transaction.subscriptionStatus === 'active' && (
                <div className="flex items-center gap-1 text-teal-600">
                  <span>Next {transaction.frequency} payment: {formatDate(transaction.nextChargeDate)}</span>
                </div>
              )}

              {/* Donor Email (for recurring) */}
              {transaction.donorEmail && (
                <div className="flex items-center gap-1">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>{transaction.donorEmail}</span>
                </div>
              )}

              {/* Card info (for recurring) */}
              {transaction.cardLastFour && (
                <div className="flex items-center gap-1 text-gray-500">
                  <span>{transaction.cardBrand ? transaction.cardBrand.charAt(0).toUpperCase() + transaction.cardBrand.slice(1) : 'Card'} ****{transaction.cardLastFour}</span>
                </div>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-1">
            {/* Edit button */}
            {onUpdate && !isEditing && (
              <button
                onClick={() => { setIsEditing(true); setIsExpanded(true) }}
                className="p-2 rounded hover:bg-gray-100 text-gray-500"
                title="Edit billing details"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            )}

            {/* Expand/Collapse button */}
            {(hasExpandableContent || isEditing) && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-2 rounded hover:bg-gray-100 text-gray-500"
                title={isExpanded ? 'Show less' : 'Show more'}
              >
                <svg className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            )}

            {/* Gift Aid PDF button */}
            {transaction.giftAid && transaction.giftAidDeclarationId && onViewGiftAid && (
              <button
                onClick={() => onViewGiftAid(transaction.giftAidDeclarationId!)}
                className="p-2 rounded hover:bg-purple-100 text-purple-600"
                title="View Gift Aid Declaration"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </button>
            )}

            {/* Receipt PDF button */}
            {transaction.receiptS3Key && onViewReceipt && (
              <button
                onClick={() => onViewReceipt(transaction.id)}
                className="p-2 rounded hover:bg-blue-100 text-blue-600"
                title="View Receipt"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
              </button>
            )}

            {/* Cancel button for active recurring subscriptions */}
            {isRecurring && transaction.subscriptionStatus === 'active' && onUpdate && (
              <button
                onClick={async () => {
                  if (window.confirm(`Cancel recurring subscription ${transaction.subscriptionReference || ''}? This will stop all future charges.`)) {
                    try {
                      await onUpdate(transaction.id, { subscriptionStatus: 'cancelled' })
                    } catch {}
                  }
                }}
                className="p-2 rounded hover:bg-gray-100 text-orange-600"
                title="Cancel subscription"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            )}

            {/* Delete button for manual transactions */}
            {isManualPlatform && onDelete && (
              <button
                onClick={onDelete}
                className="p-2 rounded hover:bg-gray-100 text-red-600"
                title="Delete"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-0 border-t border-gray-100 bg-gray-50">
          {/* Edit Form */}
          {isEditing ? (
            <div className="pt-3">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Edit Billing Details</h4>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">First Name</label>
                  <input
                    type="text"
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Email</label>
                  <input
                    type="email"
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={editPhone}
                    onChange={(e) => setEditPhone(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              </div>
              <div className="mb-3">
                <label className="block text-xs text-gray-500 mb-1">Address Line 1</label>
                <input
                  type="text"
                  value={editAddressLine1}
                  onChange={(e) => setEditAddressLine1(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              <div className="mb-3">
                <label className="block text-xs text-gray-500 mb-1">Address Line 2</label>
                <input
                  type="text"
                  value={editAddressLine2}
                  onChange={(e) => setEditAddressLine2(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">City</label>
                  <input
                    type="text"
                    value={editCity}
                    onChange={(e) => setEditCity(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Postcode</label>
                  <input
                    type="text"
                    value={editPostcode}
                    onChange={(e) => setEditPostcode(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSaveEdit}
                  disabled={isUpdating}
                  className="px-3 py-1.5 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 disabled:opacity-50"
                >
                  {isUpdating ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  disabled={isUpdating}
                  className="px-3 py-1.5 text-gray-600 text-sm hover:text-gray-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3">
                {/* Address */}
                {hasAddress && (
                  <div>
                    <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Address</h4>
                    <div className="text-sm text-gray-700">
                      {addressParts.map((part, idx) => (
                        <div key={idx}>{part}</div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Contact Info */}
                {(transaction.billing?.email || transaction.billing?.phone) && (
                  <div>
                    <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Contact</h4>
                    <div className="text-sm text-gray-700">
                      {transaction.billing?.email && <div>{transaction.billing.email}</div>}
                      {transaction.billing?.phone && <div>{transaction.billing.phone}</div>}
                    </div>
                  </div>
                )}

                {/* Reference/Notes */}
                {transaction.reference && (
                  <div>
                    <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Reference / Notes</h4>
                    <p className="text-sm text-gray-700">{transaction.reference}</p>
                  </div>
                )}

                {/* Failure reason (for failed recurring) */}
                {transaction.failureReason && (
                  <div>
                    <h4 className="text-xs font-medium text-red-500 uppercase tracking-wide mb-1">Failure Reason</h4>
                    <p className="text-sm text-red-700">{transaction.failureReason}</p>
                    {transaction.failureCount !== undefined && transaction.failureCount > 0 && (
                      <p className="text-xs text-red-500 mt-1">Failed {transaction.failureCount} time{transaction.failureCount !== 1 ? 's' : ''}</p>
                    )}
                  </div>
                )}

                {/* Parent Transaction (for recurring child charges) */}
                {transaction.parentTransactionId && (
                  <div>
                    <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Part of Subscription</h4>
                    <p className="text-sm text-gray-700">
                      {transaction.subscriptionReference || transaction.parentTransactionId}
                      <span className="text-gray-400 ml-2 text-xs font-mono">({transaction.parentTransactionId})</span>
                    </p>
                  </div>
                )}

                {/* Transaction Code */}
                {transaction.transactionCode && (
                  <div>
                    <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Transaction Code</h4>
                    <p className="text-sm text-gray-700 font-mono">{transaction.transactionCode}</p>
                  </div>
                )}

                {/* Stripe Payment Intent / External ID */}
                {transaction.externalId && (
                  <div>
                    <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      {transaction.platform === 'stripe' ? 'Stripe Payment Intent' :
                       transaction.platform === 'paypal' ? 'PayPal Transaction ID' : 'External ID'}
                    </h4>
                    <p className="text-sm text-gray-700 font-mono">{transaction.externalId}</p>
                  </div>
                )}

                {/* Stripe Subscription ID */}
                {transaction.stripeSubscriptionId && (
                  <div>
                    <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Stripe Subscription ID</h4>
                    <p className="text-sm text-gray-700 font-mono">{transaction.stripeSubscriptionId}</p>
                  </div>
                )}

                {/* Transaction ID */}
                <div>
                  <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Transaction ID</h4>
                  <p className="text-sm text-gray-700 font-mono">{transaction.id}</p>
                </div>

                {/* Created At */}
                <div>
                  <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Recorded</h4>
                  <p className="text-sm text-gray-700">
                    {new Date(transaction.createdAt).toLocaleString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-gray-200">
                {/* Generate/Regenerate Receipt Button */}
                {onGenerateReceipt && (
                  <button
                    onClick={handleGenerateReceipt}
                    disabled={isGeneratingReceipt}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 text-sm rounded hover:bg-blue-100 disabled:opacity-50"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    {isGeneratingReceipt ? 'Generating...' : (transaction.receiptS3Key ? 'Regenerate Receipt' : 'Generate Receipt')}
                  </button>
                )}

                {/* Claim/Regenerate Gift Aid Button */}
                {onCreateGiftAid && !showGiftAidForm && (
                  <button
                    onClick={() => setShowGiftAidForm(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 text-sm rounded hover:bg-purple-100"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    {transaction.giftAidDeclarationId ? 'Regenerate Gift Aid' : 'Claim Gift Aid'}
                  </button>
                )}
              </div>

              {/* Gift Aid Form */}
              {showGiftAidForm && (
                <div className="mt-4 pt-3 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Claim Gift Aid</h4>
                  <p className="text-xs text-gray-500 mb-3">
                    Enter the donor's details to claim Gift Aid (25% extra from HMRC).
                  </p>
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Title</label>
                      <select
                        value={giftAidTitle}
                        onChange={(e) => setGiftAidTitle(e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="">-</option>
                        <option value="Mr">Mr</option>
                        <option value="Mrs">Mrs</option>
                        <option value="Ms">Ms</option>
                        <option value="Miss">Miss</option>
                        <option value="Dr">Dr</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">First Name *</label>
                      <input
                        type="text"
                        value={giftAidFirstName}
                        onChange={(e) => setGiftAidFirstName(e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Last Name *</label>
                      <input
                        type="text"
                        value={giftAidLastName}
                        onChange={(e) => setGiftAidLastName(e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="block text-xs text-gray-500 mb-1">Address Line 1 *</label>
                    <input
                      type="text"
                      value={giftAidAddressLine1}
                      onChange={(e) => setGiftAidAddressLine1(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-xs text-gray-500 mb-1">Address Line 2</label>
                    <input
                      type="text"
                      value={giftAidAddressLine2}
                      onChange={(e) => setGiftAidAddressLine2(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">City *</label>
                      <input
                        type="text"
                        value={giftAidCity}
                        onChange={(e) => setGiftAidCity(e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Postcode *</label>
                      <input
                        type="text"
                        value={giftAidPostcode}
                        onChange={(e) => setGiftAidPostcode(e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-xs text-gray-500 mb-1">Email (optional)</label>
                    <input
                      type="email"
                      value={giftAidEmail}
                      onChange={(e) => setGiftAidEmail(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleCreateGiftAid}
                      disabled={!giftAidFormValid || isCreatingGiftAid}
                      className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 disabled:opacity-50"
                    >
                      {isCreatingGiftAid ? 'Submitting...' : 'Submit Gift Aid Claim'}
                    </button>
                    <button
                      onClick={() => setShowGiftAidForm(false)}
                      disabled={isCreatingGiftAid}
                      className="px-3 py-1.5 text-gray-600 text-sm hover:text-gray-800"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

const TransactionsPage: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [donationTypes, setDonationTypes] = useState<DonationType[]>([])
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Record form state
  const [showRecordForm, setShowRecordForm] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Filter state
  const [filterPlatform, setFilterPlatform] = useState<string>('all')
  const [filterDonationType, setFilterDonationType] = useState<string>('all')
  const [filterCampaign, setFilterCampaign] = useState<string>('all')
  const [filterStartDate, setFilterStartDate] = useState<string>('')
  const [filterEndDate, setFilterEndDate] = useState<string>('')
  const [filterGiftAid, setFilterGiftAid] = useState<string>('all') // 'all', 'yes', 'no'
  const [filterRecurring, setFilterRecurring] = useState<string>('all') // 'all', 'recurring', 'one_time', 'active', 'cancelled', 'payment_failed'
  const [searchQuery, setSearchQuery] = useState<string>('')

  // Sort state
  type SortOption = 'date_desc' | 'date_asc' | 'amount_desc' | 'amount_asc'
  const [sortBy, setSortBy] = useState<SortOption>('date_desc')

  const hasActiveFilters =
    filterPlatform !== 'all' ||
    filterDonationType !== 'all' ||
    filterCampaign !== 'all' ||
    filterStartDate ||
    filterEndDate ||
    filterGiftAid !== 'all' ||
    filterRecurring !== 'all'

  const clearFilters = () => {
    setFilterPlatform('all')
    setFilterDonationType('all')
    setFilterCampaign('all')
    setFilterStartDate('')
    setFilterEndDate('')
    setFilterGiftAid('all')
    setFilterRecurring('all')
  }

  // Fetch transactions from API
  const fetchTransactions = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const filter: TransactionFilter = { limit: 500 }
      if (filterPlatform !== 'all') filter.platform = filterPlatform as PaymentPlatform
      if (filterDonationType !== 'all') filter.donationTypeId = filterDonationType
      if (filterCampaign !== 'all' && filterCampaign !== 'none') filter.campaignId = filterCampaign
      if (filterStartDate) filter.startDate = filterStartDate
      if (filterEndDate) filter.endDate = filterEndDate
      if (filterGiftAid !== 'all') filter.giftAid = filterGiftAid === 'yes'

      const data = await transactionsApi.listTransactions(filter)

      // Client-side filters
      let filtered = data
      if (filterCampaign === 'none') {
        filtered = filtered.filter(t => !t.campaignId)
      }
      if (filterRecurring === 'recurring') {
        filtered = filtered.filter(t => t.frequency && t.frequency !== 'one_time')
      } else if (filterRecurring === 'one_time') {
        filtered = filtered.filter(t => !t.frequency || t.frequency === 'one_time')
      } else if (filterRecurring === 'active') {
        filtered = filtered.filter(t => t.subscriptionStatus === 'active')
      } else if (filterRecurring === 'cancelled') {
        filtered = filtered.filter(t => t.subscriptionStatus === 'cancelled')
      } else if (filterRecurring === 'payment_failed') {
        filtered = filtered.filter(t => t.subscriptionStatus === 'payment_failed')
      }

      setTransactions(filtered)
    } catch (err) {
      console.error('Failed to fetch transactions:', err)
      setError('Failed to load transactions')
    } finally {
      setLoading(false)
    }
  }, [filterPlatform, filterDonationType, filterCampaign, filterStartDate, filterEndDate, filterGiftAid, filterRecurring])

  // Fetch donation types and campaigns on mount
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [types, camps] = await Promise.all([
          donationsApi.listDonationTypes(),
          campaignsApi.listCampaigns(),
        ])
        setDonationTypes(types)
        setCampaigns(camps)
      } catch (err) {
        console.error('Failed to fetch metadata:', err)
      }
    }
    fetchMetadata()
  }, [])

  // Fetch transactions when filters change
  useEffect(() => {
    fetchTransactions()
  }, [fetchTransactions])

  // Handle save transaction
  const handleSaveTransaction = async (data: { amount: number; platform: PaymentPlatform; donationTypeId: string; date: string; reference?: string; billing?: BillingDetails; giftAid?: GiftAidDetails }) => {
    try {
      setIsSaving(true)
      setError(null)

      await transactionsApi.createTransaction({
        amount: data.amount,
        platform: data.platform,
        donationTypeId: data.donationTypeId,
        date: data.date,
        reference: data.reference,
        billing: data.billing,
        giftAid: data.giftAid,
      })

      setShowRecordForm(false)
      fetchTransactions()
    } catch (err) {
      console.error('Failed to save transaction:', err)
      setError('Failed to save transaction. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  // Handle delete transaction
  const handleDeleteTransaction = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) return

    try {
      setError(null)
      await transactionsApi.deleteTransaction(id)
      setTransactions((prev) => prev.filter((t) => t.id !== id))
    } catch (err) {
      console.error('Failed to delete transaction:', err)
      setError('Failed to delete transaction. Please try again.')
    }
  }

  // Handle view Gift Aid PDF
  const handleViewGiftAid = async (declarationId: string) => {
    try {
      const url = await transactionsApi.getGiftAidPdfUrl(declarationId)
      window.open(url, '_blank')
    } catch (err) {
      console.error('Failed to get Gift Aid PDF:', err)
      setError('Failed to load Gift Aid declaration. Please try again.')
    }
  }

  // Handle view Receipt PDF
  const handleViewReceipt = async (transactionId: string) => {
    try {
      const url = await transactionsApi.getReceiptPdfUrl(transactionId)
      window.open(url, '_blank')
    } catch (err) {
      console.error('Failed to get Receipt PDF:', err)
      setError('Failed to load receipt. Please try again.')
    }
  }

  // Handle update transaction
  const handleUpdateTransaction = async (id: string, update: TransactionUpdate) => {
    try {
      setError(null)
      await transactionsApi.updateTransaction(id, update)
      fetchTransactions() // Refresh to show updated data
    } catch (err) {
      console.error('Failed to update transaction:', err)
      setError('Failed to update transaction. Please try again.')
      throw err // Re-throw so the card knows it failed
    }
  }

  // Handle generate receipt
  const handleGenerateReceipt = async (transactionId: string) => {
    try {
      setError(null)
      await transactionsApi.generateReceipt(transactionId)
      // Show success message
      alert('Receipt generation has been queued. It will be available shortly.')
      fetchTransactions() // Refresh to show receipt status
    } catch (err) {
      console.error('Failed to generate receipt:', err)
      setError('Failed to generate receipt. Please try again.')
      throw err
    }
  }

  // Handle create Gift Aid
  const handleCreateGiftAid = async (transactionId: string, giftAid: GiftAidDetails) => {
    try {
      setError(null)
      await transactionsApi.createGiftAid(transactionId, giftAid)
      fetchTransactions() // Refresh to show Gift Aid status
    } catch (err) {
      console.error('Failed to create Gift Aid declaration:', err)
      setError('Failed to create Gift Aid declaration. Please try again.')
      throw err
    }
  }

  // Sort transactions (client-side since API returns sorted by date desc)
  // Apply search filter
  const searchedTransactions = searchQuery.trim() === '' ? transactions : transactions.filter(t => {
    const q = searchQuery.toLowerCase()
    const name = [t.billing?.firstName, t.billing?.lastName].filter(Boolean).join(' ').toLowerCase()
    return (
      name.includes(q) ||
      (t.donorEmail && t.donorEmail.toLowerCase().includes(q)) ||
      (t.subscriptionReference && t.subscriptionReference.toLowerCase().includes(q)) ||
      (t.stripeSubscriptionId && t.stripeSubscriptionId.toLowerCase().includes(q)) ||
      (t.transactionCode && t.transactionCode.toLowerCase().includes(q)) ||
      (t.externalId && t.externalId.toLowerCase().includes(q)) ||
      (t.reference && t.reference.toLowerCase().includes(q)) ||
      t.id.toLowerCase().includes(q) ||
      formatCurrency(t.amount).includes(q)
    )
  })

  const sortedTransactions = [...searchedTransactions].sort((a, b) => {
    switch (sortBy) {
      case 'date_desc':
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      case 'date_asc':
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      case 'amount_desc':
        return b.amount - a.amount
      case 'amount_asc':
        return a.amount - b.amount
      default:
        return 0
    }
  })

  // Calculate totals
  const totalAmount = transactions.reduce((sum, t) => sum + t.amount, 0)
  const totalCount = transactions.length

  // Group by platform for summary
  const platformTotals = PAYMENT_PLATFORMS.map(p => ({
    platform: p,
    amount: transactions
      .filter(t => t.platform === p.id)
      .reduce((sum, t) => sum + t.amount, 0),
    count: transactions.filter(t => t.platform === p.id).length,
  })).filter(p => p.count > 0)

  return (
    <div className="flex flex-col h-screen">
      <Navbar />
      <div className="md:container mx-auto flex-1 py-8 px-4 overflow-auto">
        <div className="w-full max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-xl font-bold text-gray-800">Transactions</h1>
              <p className="text-gray-600 text-sm mt-1">
                View all donations across all platforms
              </p>
              <p className="text-amber-600 text-xs mt-2">
                Note: PayPal transactions may take up to 3 hours to appear.
              </p>
            </div>
            {!showRecordForm && (
              <button
                onClick={() => setShowRecordForm(true)}
                className="inline-flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Record Transaction
              </button>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
              <button
                onClick={() => setError(null)}
                className="ml-2 text-red-500 hover:text-red-700"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Search */}
          <div className="mb-4">
            <div className="flex items-center border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-teal-500 focus-within:border-teal-500">
              <svg className="w-4 h-4 text-gray-400 ml-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search by name, email, reference, transaction code, Stripe subscription ID, amount..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2.5 text-sm border-0 focus:ring-0 focus:outline-none rounded-lg"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="mr-3 text-gray-400 hover:text-gray-600 shrink-0"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Failed Subscriptions Alert */}
          {(() => {
            const failedSubs = transactions.filter(t => t.subscriptionStatus === 'payment_failed')
            if (failedSubs.length === 0) return null
            return (
              <div className="mb-6 p-4 bg-red-50 border border-red-300 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <h3 className="font-semibold text-red-800">
                    {failedSubs.length} Failed Recurring {failedSubs.length === 1 ? 'Payment' : 'Payments'}
                  </h3>
                </div>
                <p className="text-sm text-red-700 mb-3">The following recurring donations have failed after 3 attempts and need attention.</p>
                <div className="space-y-3">
                  {failedSubs.map(t => {
                    const dt = donationTypes.find(d => d.id === t.donationTypeId)
                    return (
                      <div key={t.id} className="flex items-start justify-between bg-white rounded-lg p-4 border border-red-200">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-gray-900">{t.subscriptionReference || t.id.slice(0, 8)}</span>
                            <span className="text-sm bg-red-100 text-red-700 px-2 py-0.5 rounded">{t.frequency}</span>
                            <span className="text-lg font-bold text-gray-900">{formatCurrency(t.amount)}</span>
                          </div>
                          <p className="text-sm text-gray-700 mt-1">{dt?.name || t.donationTypeId}</p>
                          {t.failureReason && (
                            <div className="mt-2 p-2 bg-red-50 rounded border border-red-100">
                              <p className="text-sm text-red-700 font-medium">Error: {t.failureReason}</p>
                            </div>
                          )}
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                            {(t.billing?.firstName || t.billing?.lastName) && (
                              <span>Name: {[t.billing?.firstName, t.billing?.lastName].filter(Boolean).join(' ')}</span>
                            )}
                            {t.donorEmail && (
                              <span>Email: {t.donorEmail}</span>
                            )}
                            {t.cardLastFour && (
                              <span>Card: {t.cardBrand ? t.cardBrand.charAt(0).toUpperCase() + t.cardBrand.slice(1) : 'Card'} ****{t.cardLastFour}</span>
                            )}
                            <span>Failed {t.failureCount || 0} time{(t.failureCount || 0) !== 1 ? 's' : ''}</span>
                            {t.nextChargeDate && (
                              <span>Last retry: {formatDate(t.nextChargeDate)}</span>
                            )}
                            {t.createdAt && (
                              <span>Started: {formatDate(t.createdAt)}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={async () => {
                              if (window.confirm(`Retry recurring payment for ${t.subscriptionReference || t.id.slice(0, 8)}? This will reactivate the subscription.`)) {
                                try {
                                  await handleUpdateTransaction(t.id, { subscriptionStatus: 'active' })
                                } catch {}
                              }
                            }}
                            className="px-3 py-1.5 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                          >
                            Retry
                          </button>
                          <button
                            onClick={async () => {
                              if (window.confirm(`Cancel subscription ${t.subscriptionReference || t.id.slice(0, 8)}? This will stop all future charges.`)) {
                                try {
                                  await handleUpdateTransaction(t.id, { subscriptionStatus: 'cancelled' })
                                } catch {}
                              }
                            }}
                            className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })()}

          {/* Record Transaction Form (inline) */}
          {showRecordForm && (
            <RecordTransactionForm
              donationTypes={donationTypes}
              onSave={handleSaveTransaction}
              onCancel={() => setShowRecordForm(false)}
              isSaving={isSaving}
            />
          )}

          {/* Filters */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700">Filters</h3>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-teal-600 hover:text-teal-700"
                >
                  Clear all
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {/* Platform Filter */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Platform</label>
                <select
                  value={filterPlatform}
                  onChange={(e) => setFilterPlatform(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="all">All platforms</option>
                  {PAYMENT_PLATFORMS.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              {/* Donation Type Filter */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Donation Type</label>
                <select
                  value={filterDonationType}
                  onChange={(e) => setFilterDonationType(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="all">All types</option>
                  {donationTypes.map((dt) => (
                    <option key={dt.id} value={dt.id}>{dt.name}</option>
                  ))}
                </select>
              </div>

              {/* Campaign Filter */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Campaign</label>
                <select
                  value={filterCampaign}
                  onChange={(e) => setFilterCampaign(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="all">All campaigns</option>
                  <option value="none">No campaign</option>
                  {campaigns.map((c) => (
                    <option key={c.id} value={c.id}>{c.title}</option>
                  ))}
                </select>
              </div>

              {/* Gift Aid Filter */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Gift Aid</label>
                <select
                  value={filterGiftAid}
                  onChange={(e) => setFilterGiftAid(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="all">All</option>
                  <option value="yes">Gift Aid eligible</option>
                  <option value="no">Not eligible</option>
                </select>
              </div>

              {/* Recurring Filter */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Recurring</label>
                <select
                  value={filterRecurring}
                  onChange={(e) => setFilterRecurring(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="all">All</option>
                  <option value="recurring">Recurring only</option>
                  <option value="one_time">One-time only</option>
                  <option value="active">Active recurring</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="payment_failed">Payment failed</option>
                </select>
              </div>

              {/* Start Date */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">From</label>
                <input
                  type="date"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

              {/* End Date */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">To</label>
                <input
                  type="date"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg p-4 text-white">
              <div className="text-2xl font-bold">{formatCurrency(totalAmount)}</div>
              <div className="text-teal-100 text-sm mt-1">
                {hasActiveFilters ? 'Filtered Total' : 'Total'}
              </div>
            </div>
            <div className="bg-gradient-to-br from-gray-600 to-gray-700 rounded-lg p-4 text-white">
              <div className="text-2xl font-bold">{totalCount}</div>
              <div className="text-gray-300 text-sm mt-1">Transactions</div>
            </div>
            {platformTotals.slice(0, 2).map(({ platform, amount, count }) => {
              const pStyles = platformStyles[platform.id] || { bg: 'bg-gray-50', text: 'text-gray-600' }
              return (
                <div key={platform.id} className={`${pStyles.bg} rounded-lg p-4`}>
                  <div className={`text-2xl font-bold ${pStyles.text}`}>{formatCurrency(amount)}</div>
                  <div className="text-gray-600 text-sm mt-1">{platform.name} ({count})</div>
                </div>
              )
            })}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            </div>
          )}

          {/* Results count and Sort */}
          {!loading && (
            <>
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-gray-500">
                  {hasActiveFilters
                    ? `Showing ${sortedTransactions.length} transactions`
                    : `${transactions.length} transactions`}
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-500">Sort by:</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as SortOption)}
                    className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="date_desc">Date (Newest first)</option>
                    <option value="date_asc">Date (Oldest first)</option>
                    <option value="amount_desc">Amount (High to low)</option>
                    <option value="amount_asc">Amount (Low to high)</option>
                  </select>
                </div>
              </div>

              {/* Transaction List */}
              <div className="space-y-3">
                {sortedTransactions.map((transaction) => (
                  <TransactionCard
                    key={transaction.id}
                    transaction={transaction}
                    donationTypes={donationTypes}
                    campaigns={campaigns}
                    onDelete={() => handleDeleteTransaction(transaction.id)}
                    onUpdate={handleUpdateTransaction}
                    onGenerateReceipt={handleGenerateReceipt}
                    onCreateGiftAid={handleCreateGiftAid}
                    onViewGiftAid={handleViewGiftAid}
                    onViewReceipt={handleViewReceipt}
                  />
                ))}
              </div>
            </>
          )}

          {/* No results */}
          {!loading && sortedTransactions.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              {hasActiveFilters ? (
                <>
                  <p>No transactions match your filters.</p>
                  <button
                    onClick={clearFilters}
                    className="text-teal-600 hover:text-teal-700 text-sm mt-2"
                  >
                    Clear filters
                  </button>
                </>
              ) : (
                <>
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  <p>No transactions yet.</p>
                  <p className="text-sm mt-1">Click "Record Transaction" to add one.</p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default TransactionsPage
